/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-hooks/rules-of-hooks */
import {
  Box,
  Typography,
  TextField,
} from "@mui/material";
import { FC, useLayoutEffect, useState, useEffect } from "react";
import CustomSwitch from "../../components/CustomSwitch";
import { useDispatch } from "react-redux";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import { NotificationManager } from "../../services/api/NotificationManager";
import {
  CreateNotificationSettingsResponse,
  GetNotificationSettingsResponse,
} from "../../shared/payloads/notificationSettingsPayloads";
import { useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { NotificationDefaultSettings } from "../../types/notification-default-settings";
import {
  BorderType,
} from "../../components/NotificationEditComponents/BorderSelect";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import Tabs from "../../components/Tabs";
import { convertStringToLanguage } from "../../generic-utils";
import i18n from "../../i18n";
import { NotificationModalBase } from "../../components/notification/NotificationModalBase";
import { NotificationDesignTab } from "../../components/notification/tabs/NotificationDesignTab";

interface GoogleReviewNotificationModalProps {
  createdNotificationId: string;
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  data?: GetNotificationSettingsResponse;
  branding?: any; // Add branding prop to match pattern
}

type TabType = "rules" | "design";

const notificationStyleId = v4();

const GoogleReviewNotificationModal: FC<GoogleReviewNotificationModalProps> = ({
  createdNotificationId,
  isOpen,
  onClose,
  onSaveSuccess,
  data,
  branding,
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation();
  const language = convertStringToLanguage(i18n.language);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<TabType>("rules");

  const [minReviewStars, setMinReviewStars] = useState<number>(
    data?.minReviewStars !== undefined ? data?.minReviewStars : 4,
  );
  const [minReviews, setMinReviews] = useState<number>(
    data?.minReviews !== undefined ? data?.minReviews : 1,
  );
  const [withRepeat, setWithRepeat] = useState<boolean>(
    data?.repeatNotification !== undefined ? data?.repeatNotification : true,
  );

  const [notificationHtml, setNotificationHtml] = useState<string>("");

  // Visual settings with branding fallbacks
  const [backgroundColor, setBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.backgroundColor || null,
  );
  const [textColor, setTextColor] = useState<string | null>(
    data?.notificationVisualSettings?.textColor || null,
  );
  const [iconBackgroundColor, setIconBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.iconBackgroundColor || null,
  );
  const [iconColor, setIconColor] = useState<string | null>(
    data?.notificationVisualSettings?.iconColor || null,
  );
  const [borderRadius, setBorderRadius] = useState<BorderType | null>(
    data?.notificationVisualSettings?.roundness || null,
  );
  const [icon, setIcon] = useState<string | null>(
    data?.notificationVisualSettings?.icon || null,
  );

  const [backgroundOpacity, setBackgroundOpacity] = useState<number | null>(
    data?.notificationVisualSettings?.backgroundOpacity || null,
  );
  const [active, setActive] = useState<"classic" | "small" | "compact">(
    data?.notificationVisualSettings?.activeSize || "classic",
  );
  const [showIcon, setShowIcon] = useState<boolean>(
    data?.notificationVisualSettings?.isShowingIcon !== null &&
      typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
      ? !!data?.notificationVisualSettings?.isShowingIcon
      : true,
  );
  const [iconBackgroundRadius, setIconBackgroundRadius] = useState<
    number | null
  >(data?.notificationVisualSettings?.iconBackgroundRadius || null);

  const [detectedChange, setDetectedChange] = useState<boolean>(false);

  // Change detection logic
  useEffect(() => {
    const defaultBackgroundColor =
      data?.notificationVisualSettings?.backgroundColor ??
      branding?.backgroundColor ??
      NotificationDefaultSettings.BackgroundColor;

    const defaultTextColor =
      data?.notificationVisualSettings?.textColor ??
      branding?.textColor ??
      NotificationDefaultSettings.TextColor;

    const withRepeatDefault =
      data?.repeatNotification !== undefined ? data?.repeatNotification : true;

    const minReviewStarsDefault =
      data?.minReviewStars !== undefined ? data?.minReviewStars : 4;

    const minReviewsDefault =
      data?.minReviews !== undefined ? data?.minReviews : 1;

    const activeDefault =
      data?.notificationVisualSettings?.activeSize ?? branding?.activeSize;

    const iconColorDefault =
      data?.notificationVisualSettings?.iconColor ??
      branding?.iconColor ??
      NotificationDefaultSettings.IconColor;

    const showIconDefault =
      data?.notificationVisualSettings?.isShowingIcon !== null &&
      typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true;

    const defaultIconBackgroundColor =
      data?.notificationVisualSettings?.iconBackgroundColor ??
      branding?.iconBackgroundColor ??
      NotificationDefaultSettings.IconBackgroundColor;

    if (
      borderRadius !== null &&
      (data?.notificationVisualSettings?.roundness !== null &&
      data?.notificationVisualSettings?.roundness !== undefined
        ? borderRadius !== data?.notificationVisualSettings?.roundness
        : borderRadius !== branding?.borderRadius)
    ) {
      setDetectedChange(true);
    } else if (
      backgroundOpacity !== null &&
      (data?.notificationVisualSettings?.backgroundOpacity !== null &&
      data?.notificationVisualSettings?.backgroundOpacity !== undefined
        ? backgroundOpacity !==
          data?.notificationVisualSettings?.backgroundOpacity
        : backgroundOpacity !== branding?.backgroundOpacity)
    ) {
      setDetectedChange(true);
    } else if (backgroundColor && backgroundColor !== defaultBackgroundColor) {
      setDetectedChange(true);
    } else if (textColor && textColor !== defaultTextColor) {
      setDetectedChange(true);
    } else if (activeDefault && activeDefault !== active) {
      setDetectedChange(true);
    } else if (showIcon !== showIconDefault) {
      setDetectedChange(true);
    } else if (
      iconBackgroundColor &&
      iconBackgroundColor !== defaultIconBackgroundColor
    ) {
      setDetectedChange(true);
    } else if (
      iconBackgroundRadius &&
      (data?.notificationVisualSettings?.iconBackgroundRadius !== null &&
      data?.notificationVisualSettings?.iconBackgroundRadius !== undefined
        ? iconBackgroundRadius !==
          data?.notificationVisualSettings?.iconBackgroundRadius
        : iconBackgroundRadius !== branding?.iconBackgroundRadius)
    ) {
      setDetectedChange(true);
    } else if (iconColor && iconColor !== iconColorDefault) {
      setDetectedChange(true);
    } else if (
      icon &&
      (data?.notificationVisualSettings?.icon
        ? icon !== data?.notificationVisualSettings?.icon
        : icon !== NotificationDefaultSettings.Icon || null)
    ) {
      setDetectedChange(true);
    } else if (minReviews !== minReviewsDefault) {
      setDetectedChange(true);
    } else if (minReviewStars !== minReviewStarsDefault) {
      setDetectedChange(true);
    } else if (withRepeatDefault !== withRepeat) {
      setDetectedChange(true);
    } else {
      setDetectedChange(false);
    }
  }, [
    borderRadius,
    minReviews,
    minReviewStars,
    withRepeat,
    active,
    icon,
    textColor,
    iconColor,
    backgroundColor,
    backgroundOpacity,
    showIcon,
    iconBackgroundColor,
    iconBackgroundRadius,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.iconColor,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.roundness,
    branding?.brandingIsShowingIcon,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.iconColor,
    branding?.activeSize,
    branding?.iconBackgroundColor,
    branding?.iconBackgroundRadius,
  ]);

  const onCloseAndClearInputs = () => {
    setMinReviewStars(
      data?.minReviewStars !== undefined ? data?.minReviewStars : 4,
    );
    setMinReviews(data?.minReviews !== undefined ? data?.minReviews : 1);
    setWithRepeat(
      data?.repeatNotification !== undefined ? data?.repeatNotification : true,
    );
    onClose();
  };

  // Initialize visual settings with branding fallbacks
  useEffect(() => {
    setBackgroundColor(
      data?.notificationVisualSettings?.backgroundColor ??
        branding?.backgroundColor ??
        null,
    );
    setTextColor(
      data?.notificationVisualSettings?.textColor ??
        branding?.textColor ??
        null,
    );
    setIconBackgroundColor(
      data?.notificationVisualSettings?.iconBackgroundColor ??
        branding?.iconBackgroundColor ??
        null,
    );
    setBorderRadius(
      data?.notificationVisualSettings?.roundness ??
        branding?.borderRadius ??
        null,
    );
    setBackgroundOpacity(
      data?.notificationVisualSettings?.backgroundOpacity ??
        branding?.backgroundOpacity ??
        null,
    );
    setActive(
      data?.notificationVisualSettings?.activeSize ||
        branding?.activeSize ||
        "classic",
    );
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : true,
    );
    setIcon(data?.notificationVisualSettings?.icon ?? null);
    setIconBackgroundRadius(
      data?.notificationVisualSettings?.iconBackgroundRadius ??
        branding?.iconBackgroundRadius ??
        null,
    );
    setIconColor(
      data?.notificationVisualSettings?.iconColor ??
        branding?.iconColor ??
        null,
    );
  }, [
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconColor,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.iconBackgroundColor,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.activeSize,
    branding?.iconBackgroundRadius,
    branding?.iconColor,
  ]);

  useLayoutEffect(() => {
    const createNotificationPreviewHtml = async () => {
      const result = await window.getNotificationPreview(
        {
          campaign: {
            branding: {
              textColor: branding?.textColor,
              backgroundColor: branding?.backgroundColor,
              borderRadius: branding?.borderRadius,
              backgroundOpacity: branding?.backgroundOpacity,
              iconColor: branding?.iconColor,
              iconBackgroundColor: branding?.iconBackgroundColor,
              iconBackgroundRadius: branding?.iconBackgroundRadius,
              activeSize: branding?.activeSize,
              brandingIsShowingIcon: branding?.brandingIsShowingIcon,
              logoDisplay: branding?.logoDisplay,
              logo: branding?.logo,
              logoBackgroundColor: branding?.logoBackgroundColor,
              logoText: branding?.logoText,
            },
            notificationVisualSettings: {
              roundness: 1,
            },
          },
          settings: {
            type: "googleReviews",
            notificationVisualSettings: {
              backgroundColor:
                backgroundColor ??
                data?.notificationVisualSettings?.backgroundColor ??
                branding?.backgroundColor ??
                "",
              textColor:
                textColor ??
                data?.notificationVisualSettings?.textColor ??
                branding?.textColor ??
                "",
              iconBackgroundColor:
                iconBackgroundColor ??
                data?.notificationVisualSettings?.iconBackgroundColor ??
                branding?.iconBackgroundColor ??
                "",
              iconColor:
                iconColor ??
                data?.notificationVisualSettings?.iconColor ??
                branding?.iconColor ??
                "",
              icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
              roundness:
                borderRadius ??
                data?.notificationVisualSettings?.roundness ??
                branding?.borderRadius ??
                0,
              linkTextColor: "",
              linkButtonBackgroundColor: "",
              backgroundOpacity:
                backgroundOpacity ??
                data?.notificationVisualSettings?.backgroundOpacity ??
                branding?.backgroundOpacity ??
                1,
              activeSize:
                active ??
                data?.notificationVisualSettings?.activeSize ??
                branding?.activeSize ??
                "classic",
              isShowingIcon:
                showIcon ??
                data?.notificationVisualSettings?.isShowingIcon ??
                branding?.brandingIsShowingIcon ??
                true,
              iconBackgroundRadius:
                iconBackgroundRadius ??
                data?.notificationVisualSettings?.iconBackgroundRadius ??
                branding?.iconBackgroundRadius ??
                0,
            },
          },
        },
        "googleReviews",
        undefined,
        t("campaign.notifications.googleReviews.previewText1") ?? "",
        undefined,
        language,
        undefined,
        undefined,
        t("campaign.notifications.googleReviews.previewText1") ?? "",
        minReviewStars * 20,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        notificationStyleId,
      );
      // Transforms object htmldivelement to pure string
      const cleanHtmlString = DOMPurify.sanitize(result, {
        USE_PROFILES: { html: true, svg: true },
      });
      setNotificationHtml(cleanHtmlString);
    };

    createNotificationPreviewHtml();
  }, [
    active,
    backgroundColor,
    backgroundOpacity,
    borderRadius,
    branding?.activeSize,
    branding?.backgroundColor,
    branding?.backgroundOpacity,
    branding?.borderRadius,
    branding?.brandingIsShowingIcon,
    branding?.iconBackgroundColor,
    branding?.iconBackgroundRadius,
    branding?.iconColor,
    branding?.textColor,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconColor,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.textColor,
    icon,
    iconBackgroundColor,
    iconBackgroundRadius,
    iconColor,
    minReviewStars,
    showIcon,
    textColor,
  ]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue as TabType);
  };

  const onNotificationCreate = async (): Promise<string | undefined> => {
    if (id) {
      try {
        const _notificationManager = NotificationManager.getManager();
        const result: CreateNotificationSettingsResponse =
          await _notificationManager.createNotificationSettingsAsync(
            id,
            "googleReviews",
          );
        if (result.id) {
          return result.id;
        } else {
          throw new Error(t("popups.cannotCreateNotification") ?? "");
        }
      } catch (e: any) {
        showFlashMessageWithTimeout(dispatch, e.toString(), "error");
      }
    }
  };

  const onSave = async () => {
    try {
      setIsLoading(true);
      const _notificationManager = NotificationManager.getManager();
      let newCreatedNotificationId = undefined;
      if ("googleReviews" !== data?.type) {
        await _notificationManager.deleteNotificationSettings(
          createdNotificationId,
        );
        newCreatedNotificationId = await onNotificationCreate();
      }

      await _notificationManager.updateNotificationSettingsAsync({
        id: newCreatedNotificationId ?? createdNotificationId,
        isActive: true,
        repeatNotification: withRepeat,
        minReviews: minReviews,
        minReviewStars: minReviewStars,
        notificationVisualSettings: {
          backgroundColor: backgroundColor ?? branding?.backgroundColor ?? "",
          textColor: textColor ?? branding?.textColor ?? "",
          iconBackgroundColor:
            iconBackgroundColor ?? branding?.iconBackgroundColor ?? "",
          iconColor: iconColor ?? branding?.iconColor ?? "",
          icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
          roundness: borderRadius ?? branding?.borderRadius ?? 0,
          linkTextColor: "",
          linkButtonBackgroundColor: "",
          backgroundOpacity:
            backgroundOpacity ?? branding?.backgroundOpacity ?? 1,
          activeSize: active ?? branding?.activeSize ?? "classic",
          isShowingIcon: showIcon ?? branding?.brandingIsShowingIcon ?? true,
          iconBackgroundRadius:
            iconBackgroundRadius ?? branding?.iconBackgroundRadius ?? 0,
        },
      });
      setIsLoading(false);
      onSaveSuccess?.();
      onCloseAndClearInputs();
    } catch (e: any) {
      setIsLoading(false);
      showFlashMessageWithTimeout(dispatch, e.toString(), "error");
    }
  };

  const onReset = () => {
    setActive(
      data?.notificationVisualSettings?.activeSize ??
        branding?.activeSize ??
        "classic",
    );
    setBackgroundColor(null);
    setTextColor(null);
    setBorderRadius(null);
    setBackgroundOpacity(null);
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true,
    );
    setIconBackgroundColor(null);
    setIconBackgroundRadius(null);
    setIconColor(null);
    setIcon(null);
    setMinReviews(data?.minReviews !== undefined ? data?.minReviews : 1);
    setMinReviewStars(
      data?.minReviewStars !== undefined ? data?.minReviewStars : 4,
    );
    setWithRepeat(
      data?.repeatNotification !== undefined ? data?.repeatNotification : true,
    );
  };

  // Custom rules tab content for Google Reviews
  const GoogleReviewRulesTabContent = () => (
    <div>
      <Typography
        style={{
          fontSize: 18,
          marginTop: 16,
          marginBottom: 16,
          fontWeight: "bold",
        }}
      >
        {t("campaign.notifications.settings.displayRules.show")}
      </Typography>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          flexWrap: "wrap",
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: 104,
          }}
        >
          <Box
            style={{ marginBottom: 16, display: "flex", alignItems: "center" }}
          >
            <Typography
              style={{
                flex: 1,
                fontSize: 16,
              }}
            >
              {t(
                "campaign.notifications.settings.displayRules.googleReviews.maxReviews",
              )}
            </Typography>
            <TextField
              style={{ width: 108, marginRight: 12 }}
              type='number'
              variant='outlined'
              value={minReviews}
              onChange={event => {
                setMinReviews(parseInt(event.target.value, 10));
              }}
              InputProps={{
                inputProps: { min: 0 },
                style: { height: 44 },
              }}
            />
            <Box style={{ width: 72 }} />
          </Box>
          <Box style={{ display: "flex", alignItems: "center" }}>
            <Typography
              style={{
                flex: 1,
                fontSize: 16,
              }}
            >
              {t(
                "campaign.notifications.settings.displayRules.googleReviews.reviewsWithRating",
              )}
            </Typography>
            <TextField
              style={{ width: 108, marginRight: 12 }}
              type='number'
              variant='outlined'
              value={minReviewStars}
              onChange={event => {
                const result = parseInt(event.target.value, 10);
                if (result >= 5) {
                  setMinReviewStars(5);
                } else {
                  setMinReviewStars(result);
                }
              }}
              InputProps={{
                inputProps: { min: 0 },
                style: { height: 44 },
              }}
            />
            <Typography
              style={{
                fontSize: 16,
                width: 72,
              }}
            >
              {t(
                "campaign.notifications.settings.displayRules.googleReviews.stars",
              )}
            </Typography>
          </Box>
        </Box>
        <Box style={{ flex: 1 }}>
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              height: 52,
            }}
          >
            <Typography style={{ fontSize: 16 }}>
              {t(
                "campaign.notifications.settings.displayRules.repeatNotification",
              )}
            </Typography>
            <Box width='35%'>
              <CustomSwitch
                checked={withRepeat}
                onSwitch={() => setWithRepeat(!withRepeat)}
                disabled={false}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );

  const tabs = [
    {
      value: "rules",
      label: t("campaign.notifications.settings.displayRules.title"),
      content: <GoogleReviewRulesTabContent />,
    },
    {
      value: "design",
      label: t("campaign.notifications.settings.design.title"),
      content: (
        <NotificationDesignTab
          active={active}
          data={data}
          backgroundColor={backgroundColor}
          backgroundOpacity={backgroundOpacity}
          borderRadius={borderRadius}
          branding={branding}
          iconBackgroundColor={iconBackgroundColor}
          iconBackgroundRadius={iconBackgroundRadius}
          setActive={setActive}
          setBackgroundColor={setBackgroundColor}
          setBackgroundOpracity={setBackgroundOpacity}
          setBorderRadius={setBorderRadius}
          setIconBackgroundColor={setIconBackgroundColor}
          setIconBackgroundRadius={setIconBackgroundRadius}
          setShowIcon={setShowIcon}
          setTextColor={setTextColor}
          showIcon={showIcon}
          textColor={textColor}
          disableIcon={false}
          icon={icon}
          iconColor={iconColor}
          setIcon={setIcon}
          setIconColor={setIconColor}
        />
      ),
    },
  ];

  return (
    <NotificationModalBase
      title={t("campaign.notifications.googleReviews.title")}
      detectedChange={detectedChange}
      isOpen={isOpen}
      isNewNotification={!data}
      notificationHtml={notificationHtml}
      onClose={onClose}
      onReset={onReset}
      onSave={onSave}
      previewModalOpen={previewModalOpen}
      setPreviewModalOpen={setPreviewModalOpen}
    >
      <Tabs onTabChange={handleChange} selectedTab={selectedTab} tabs={tabs} />
    </NotificationModalBase>
  );
};

export default GoogleReviewNotificationModal;
